import * as React from 'react';
import { connect } from 'react-redux';
import {
    QuestionFormBaseState,
    QuestionFormBaseProps,
    QuestionFormBase,
    mapStateToQuestionFormBaseProps,
    mapDispatchToQuestionFormBaseProps
} from '../questionType/QuestionFormBase';
import Toggle from '../../../common/Toggle';
import ProbeSettings from '../common/ProbeSettings';
import { QuestionTypesMap } from '@nexxt/common/questionTypes';
import { ConversationalSettings, TrainedModel } from '@nexxt/common/types';
import { ConversationalTarget as IConversationalTarget } from '@nexxt/common/types';
import { TARGET_ACTIONS } from '@nexxt/common/types';
import { SortableContainer, arrayMove } from 'react-sortable-hoc';
import ConversationalTarget from './ConversationalTarget';
import * as uuid from 'uuid';
import QualityControlSettings from '../common/QualityControlSettings';
import CustomTrainedModelSettings from '../common/CustomTrainedModelSettings';
import { projectLanguage } from '@nexxt/common/constants';
import { UilPlus } from '@iconscout/react-unicons';
import TrainedModelEditor from './TrainedModel/TrainedModelEditor';
import * as tokenService from '@nexxt/common/firebase/auth/userDetails';

interface ConversationalSettingsTabProps extends QuestionFormBaseProps {}

interface ConversationalSettingsTabState extends QuestionFormBaseState {}

const SortableContainerWrapper = SortableContainer(({ children }) => (
    <div>{children}</div>
));

class ConversationalSettingsTab extends QuestionFormBase<
    ConversationalSettingsTabProps,
    ConversationalSettingsTabState
> {
    constructor(p: QuestionFormBaseProps) {
        super(p);
    }

    setConversationalSettings = (
        conversationalSettings: ConversationalSettings
    ) => {
        this.setQuestion('conversationalSettings', {
            ...this.state.question.conversationalSettings,
            ...conversationalSettings
        });
    };

    addNewTarget = () => {
        const targets =
            this.state.question?.conversationalSettings?.smartProbeSettings
                ?.targets || [];
        const emptyTarget: IConversationalTarget = {
            id: uuid.v4(),
            label: '',
            action: TARGET_ACTIONS.TRIGGER_ON_DETECTION,
            priority: null,
            detection_examples: [],
            detection_counterexamples: []
        };

        targets.push(emptyTarget);

        this.setConversationalSettings({
            ...this.state.question?.conversationalSettings,
            smartProbeSettings: {
                ...this.state.question?.conversationalSettings
                    ?.smartProbeSettings,
                targets
            }
        });
    };

    handleTarget = (index: number, keyName: string, value: any) => {
        const targets =
            this.state.question?.conversationalSettings?.smartProbeSettings
                .targets;
        const currentTarget = { ...targets[index], [keyName]: value };

        this.setConversationalSettings({
            ...this.state.question?.conversationalSettings,
            smartProbeSettings: {
                ...this.state.question?.conversationalSettings
                    ?.smartProbeSettings,
                targets: [
                    ...targets.slice(0, index),
                    currentTarget,
                    ...targets.slice(index + 1)
                ]
            }
        });
    };

    handleDeleteTarget = (index) => {
        const targets =
            this.state.question?.conversationalSettings?.smartProbeSettings
                .targets;

        this.setConversationalSettings({
            ...this.state.question?.conversationalSettings,
            smartProbeSettings: {
                ...this.state.question?.conversationalSettings
                    ?.smartProbeSettings,
                targets: [
                    ...targets.slice(0, index),
                    ...targets.slice(index + 1)
                ]
            }
        });
    };

    isTargetPriorityInOrder = (targets) => {
        return targets.every(
            (target, index) => target.priority == String(index)
        );
    };

    /** SORT Drag&Drop */
    onSortEnd = ({ oldIndex, newIndex }) => {
        const { question } = this.state;
        const enableTargetPriority =
            question?.conversationalSettings?.smartProbeSettings
                ?.enableTargetPriority;
        let newTargets = arrayMove(
            question?.conversationalSettings?.smartProbeSettings.targets,
            oldIndex,
            newIndex
        );
        if (
            enableTargetPriority &&
            this.isTargetPriorityInOrder(
                question?.conversationalSettings?.smartProbeSettings.targets
            )
        ) {
            newTargets = newTargets.map((target, index) => ({
                ...target,
                priority: index
            }));
        }

        this.setConversationalSettings({
            ...this.state.question?.conversationalSettings,
            smartProbeSettings: {
                ...this.state.question?.conversationalSettings
                    ?.smartProbeSettings,
                targets: newTargets
            }
        });
    };

    /**
     * Grabs trained model from selected options or creates/updates a new custom trained model and assigns it as selected trained model for @langId in conversationalSettings.
     */
    handleTrainedModelChange = (trainedModel: TrainedModel, langId: string) => {
        this.setConversationalSettings({
            ...this.state.question?.conversationalSettings,
            smartProbeSettings: {
                ...this.state.question?.conversationalSettings
                    ?.smartProbeSettings,
                trainedModelMapping: {
                    ...this.state.question?.conversationalSettings
                        ?.smartProbeSettings?.trainedModelMapping,
                    [langId]: trainedModel
                }
            }
        });
    };

    render() {
        const { question } = this.state;
        const questionTypeItem = QuestionTypesMap[question?.type];
        const { smartProbe, qualityControlSettings } = questionTypeItem;
        const selectedQuestionObjectives =
            question.conversationalSettings?.smartProbeSettings?.researchContext
                ?.question_objectives;

        const questionLangs = question.questionTexts.map((text) =>
            projectLanguage.find((l) => l.value == text.language)
        );
        const tenantId = tokenService.getTenantId();
        const allowCustomTrainedModel = [246, 508].includes(tenantId);

        const useCustomTrainedModel = Boolean(
            question?.conversationalSettings?.dialoguePolicy
        );
        return (
            <div className="project-add--question-builder">
                <div className="builder-body">
                    {allowCustomTrainedModel && (
                        <div>
                            <label className="font-semibold mb-1">
                                Enable Sensory Bot
                            </label>
                            <Toggle
                                className="without-label -mt-3"
                                onChange={(value) =>
                                    this.setConversationalSettings({
                                        ...question?.conversationalSettings,
                                        dialoguePolicy: value
                                    })
                                }
                                checked={useCustomTrainedModel}
                            />
                        </div>
                    )}
                    {allowCustomTrainedModel &&
                        useCustomTrainedModel &&
                        question && (
                            <CustomTrainedModelSettings
                                question={question}
                                setQuestion={this.setQuestionAsQuestion}
                            />
                        )}

                    {!useCustomTrainedModel && (
                        <QualityControlSettings
                            settings={qualityControlSettings}
                            question={question}
                            setQuestion={this.setQuestionAsQuestion}
                            disabled={!this.props.editEnabled}
                        />
                    )}

                    {!useCustomTrainedModel && (
                        <ProbeSettings
                            settings={smartProbe}
                            question={question}
                            setQuestion={this.setQuestionAsQuestion}
                            disabled={!this.props.editEnabled}
                        />
                    )}

                    {!useCustomTrainedModel &&
                    question?.conversationalSettings?.smartProbeSettings
                        ?.enableResearchContext ? (
                        <div className="form-group flex flex-col gap-4 p-4">
                            <div>
                                <div className="font-semibold mb-2">
                                    Research Objectives
                                </div>
                                <p>
                                    Specify in 1-2 sentences what the question
                                    objectives are, as though you were briefing
                                    a colleague who will be interviewing people
                                    for you.
                                </p>
                            </div>
                            <div className="flex flex-row">
                                <div className="font-semibold w-40">
                                    Question Objective Summary:
                                </div>
                                <div className="flex-1">
                                    <textarea
                                        className="smartProbe--textarea"
                                        placeholder={
                                            'We recommend you start the sentence with “We want to understand...”\n\n For example, “We want to understand what led consumers to opening their bank account with their bank, and how they heard about the bank. We are NOT concerned with which specific features appealed to them, but rather how they heard about the bank and decided to put their trust in them.”'
                                        }
                                        value={selectedQuestionObjectives}
                                        onChange={(e) =>
                                            this.setConversationalSettings({
                                                ...question?.conversationalSettings,
                                                smartProbeSettings: {
                                                    ...question
                                                        ?.conversationalSettings
                                                        ?.smartProbeSettings,
                                                    researchContext: {
                                                        ...question
                                                            ?.conversationalSettings
                                                            ?.smartProbeSettings
                                                            ?.researchContext,
                                                        question_objectives:
                                                            e.target.value
                                                    }
                                                }
                                            })
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {!useCustomTrainedModel &&
                    question?.conversationalSettings?.smartProbeSettings
                        ?.enableTrainedModels ? (
                        <div className="form-group flex flex-col gap-4 p-4">
                            <div>
                                <div className="font-semibold mb-2">
                                    Trained Models
                                </div>
                                <p>
                                    For each language where you would like to
                                    use a Trained Model, select a trained model
                                    below. Please contact support for more
                                    advanced configuration or for saving trained
                                    models.
                                </p>
                            </div>
                            <div>
                                {questionLangs.map((lang) => (
                                    <TrainedModelEditor
                                        key={lang.value}
                                        lang={lang}
                                        question={question}
                                        trainedModelSummaries={
                                            this.props.trainedModelSummaries
                                        }
                                        handleTrainedModelChange={
                                            this.handleTrainedModelChange
                                        }
                                        editEnabled={this.props.editEnabled}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : null}

                    {!useCustomTrainedModel &&
                    question?.conversationalSettings?.smartProbeSettings
                        ?.enableTargets ? (
                        <div className="form-group flex flex-col gap-4 p-4">
                            <div>
                                <div className="font-semibold mb-2">
                                    Conversational Targets
                                </div>
                                <p>
                                    For each target, just provide a 1-2 word
                                    label: this could be a topic, phrase, or
                                    name (including people/brands/products/etc.)
                                    that you're interested in! You can customize
                                    the action for each target, which determines
                                    SmartProbe's behaviour: you can choose to
                                    probe when a target is detected, or when
                                    it's not detected, or you can avoid the
                                    target altogether. You can also choose no
                                    action, in which case you can still use the
                                    target as an inca variable, but it won't
                                    affect SmartProbe's questions! Targets will
                                    be automatically detected in any language,
                                    but you may also provide examples (or
                                    counter-examples) if your targets are not
                                    being detected reliably. Add conversational
                                    targets that represent any topic, phrase, or
                                    name (including people/brands/products/etc.)
                                    that are of interest to a researcher, which
                                    a respondent might mention in their
                                    open-ended response. Detected targets can be
                                    incorporated into surveys and analyzed like
                                    any other quantitative variable; and targets
                                    can impact SmartProbe’s behavior through
                                    their actions.
                                </p>
                            </div>
                            <div className="flex flex-row gap-4 w-1/2">
                                <div className="flex-1">
                                    <label className="font-semibold mb-1">
                                        Enable Target Priority
                                    </label>
                                    <p>
                                        When multiple targets are triggered, by
                                        default SmartProbe chooses one randomly
                                        to probe on. You can set target priority
                                        to override this default behaviour.
                                    </p>
                                </div>
                                <div className="w-10">
                                    <Toggle
                                        className="without-label -mt-3"
                                        onChange={(value) =>
                                            this.setConversationalSettings({
                                                ...question?.conversationalSettings,
                                                smartProbeSettings: {
                                                    ...question
                                                        ?.conversationalSettings
                                                        ?.smartProbeSettings,
                                                    enableTargetPriority: value
                                                }
                                            })
                                        }
                                        checked={
                                            question?.conversationalSettings
                                                ?.smartProbeSettings
                                                ?.enableTargetPriority
                                        }
                                    />
                                </div>
                            </div>
                            <div>
                                <SortableContainerWrapper
                                    axis="y"
                                    onSortEnd={this.onSortEnd}
                                    helperClass="options--drag--helper"
                                    useDragHandle={true}
                                >
                                    {question.conversationalSettings?.smartProbeSettings?.targets?.map(
                                        (target, index) => {
                                            return (
                                                <ConversationalTarget
                                                    index={index}
                                                    key={target.id}
                                                    order={index + 1}
                                                    target={target}
                                                    totalTargets={
                                                        question
                                                            .conversationalSettings
                                                            ?.smartProbeSettings
                                                            ?.targets?.length
                                                    }
                                                    enablePriority={
                                                        question
                                                            ?.conversationalSettings
                                                            ?.smartProbeSettings
                                                            ?.enableTargetPriority
                                                    }
                                                    isSmartProbeEnabled={
                                                        question
                                                            .conversationalSettings
                                                            ?.smartProbeSettings
                                                            ?.enableSmartProbe
                                                    }
                                                    disabled={
                                                        !this.props.editEnabled
                                                    }
                                                    onTargetChange={(
                                                        keyName,
                                                        value
                                                    ) =>
                                                        this.handleTarget(
                                                            index,
                                                            keyName,
                                                            value
                                                        )
                                                    }
                                                    onTargetDelete={() =>
                                                        this.handleDeleteTarget(
                                                            index
                                                        )
                                                    }
                                                />
                                            );
                                        }
                                    )}
                                </SortableContainerWrapper>
                                <button
                                    className="btn btn--md btn--blue flex flex-row gap-4"
                                    onClick={this.addNewTarget}
                                    disabled={!this.props.editEnabled}
                                >
                                    <UilPlus size="14" />
                                    <span className="title">Add targets</span>
                                </button>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        );
    }
}

export default connect(
    mapStateToQuestionFormBaseProps,
    mapDispatchToQuestionFormBaseProps
)(ConversationalSettingsTab);
